// =============================================================================
// Admin User Detail Page — View, edit, and manage a single admin user
// =============================================================================

import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Spinner,
} from '@tkpod/ui';
import { api } from '@/lib/api';
import type {
  AdminUserDetail,
  AdminRoleSummary,
  UpdateAdminUserRequest,
  PermissionGroup,
} from '@tkpod/shared';

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

interface UserDetailResponse {
  data: AdminUserDetail;
}

interface RolesListResponse {
  data: AdminRoleSummary[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface PermissionsResponse {
  groups: PermissionGroup[];
}

interface ProviderSummary {
  id: string;
  name: string;
  slug: string;
}

interface ProvidersListResponse {
  data: ProviderSummary[];
}

interface CreateProviderResponse {
  data: ProviderSummary;
}

// ---------------------------------------------------------------------------
// Edit form state
// ---------------------------------------------------------------------------

interface EditFormState {
  roleId: string;
  department: string;
  additionalPermissions: string;
  providerId: string;
}

interface NewProviderFormState {
  name: string;
  type: string;
  contactEmail: string;
}

const EMPTY_NEW_PROVIDER: NewProviderFormState = {
  name: '',
  type: 'EXTERNAL_API',
  contactEmail: '',
};

const CREATE_NEW_PROVIDER_VALUE = '__CREATE_NEW__';

// ---------------------------------------------------------------------------
// Password validation — mirrors backend ResetAdminUserPasswordDto regex
// ---------------------------------------------------------------------------

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;

function isPasswordValid(password: string): boolean {
  return PASSWORD_REGEX.test(password);
}

interface ResetPasswordState {
  password: string;
  forcePasswordChange: boolean;
}

const EMPTY_RESET_PASSWORD: ResetPasswordState = {
  password: '',
  forcePasswordChange: false,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function displayName(user: AdminUserDetail): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : user.email;
}

function initials(user: AdminUserDetail): string {
  const first = user.firstName?.[0] ?? '';
  const last = user.lastName?.[0] ?? '';
  if (first || last) return (first + last).toUpperCase();
  return user.email[0]?.toUpperCase() ?? '?';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // -- Local state ----------------------------------------------------------
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<EditFormState>({
    roleId: '',
    department: '',
    additionalPermissions: '',
    providerId: '',
  });
  const [showNewProviderForm, setShowNewProviderForm] = useState(false);
  const [newProviderForm, setNewProviderForm] = useState<NewProviderFormState>(EMPTY_NEW_PROVIDER);
  const [confirmAction, setConfirmAction] = useState<
    'suspend' | 'activate' | 'remove' | null
  >(null);
  const [resetPassword, setResetPassword] = useState<ResetPasswordState | null>(
    null,
  );

  // -- Queries --------------------------------------------------------------
  const userQuery = useQuery<UserDetailResponse>({
    queryKey: ['admin-users', id],
    queryFn: () => api.get<UserDetailResponse>(`/api/v1/admin/users/${id}`),
    enabled: !!id,
  });

  const rolesQuery = useQuery<RolesListResponse>({
    queryKey: ['admin-roles'],
    queryFn: () => api.get<RolesListResponse>('/api/v1/admin/roles'),
    enabled: isEditing,
  });

  const providersQuery = useQuery<ProvidersListResponse>({
    queryKey: ['providers-list'],
    queryFn: () => api.get<ProvidersListResponse>('/api/v1/admin/providers'),
    enabled: isEditing,
  });

  const permissionsQuery = useQuery<PermissionsResponse>({
    queryKey: ['admin-roles-permissions'],
    queryFn: () => api.get<PermissionsResponse>('/api/v1/admin/roles/permissions'),
    enabled: !!userQuery.data,
  });

  const user = userQuery.data?.data;
  const roles = rolesQuery.data?.data ?? [];
  const providers = providersQuery.data?.data ?? [];

  const selectedEditRole = roles.find((r) => r.id === form.roleId);
  const isEditProviderRole = selectedEditRole?.slug === 'provider_ops';

  // -- Group effective permissions by resource ------------------------------
  const groupedPermissions = useMemo<
    Array<PermissionGroup>
  >(() => {
    if (!user || !permissionsQuery.data) return [];

    const groups = permissionsQuery.data.groups;
    return groups
      .map((group: PermissionGroup) => ({
        ...group,
        permissions: group.permissions.filter(
          (p: PermissionGroup['permissions'][number]) =>
            user.effectivePermissions.includes(p.key),
        ),
      }))
      .filter((group: PermissionGroup) => group.permissions.length > 0);
  }, [user, permissionsQuery.data]);

  // Permissions not in any known group
  const ungroupedPermissions = useMemo<string[]>(() => {
    if (!user || !permissionsQuery.data) return user?.effectivePermissions ?? [];

    const allKnownKeys = new Set(
      permissionsQuery.data.groups.flatMap((g: PermissionGroup) =>
        g.permissions.map((p: PermissionGroup['permissions'][number]) => p.key),
      ),
    );
    return user.effectivePermissions.filter((p: string) => !allKnownKeys.has(p));
  }, [user, permissionsQuery.data]);

  // -- Mutations ------------------------------------------------------------
  const updateMutation = useMutation({
    mutationFn: (body: UpdateAdminUserRequest) =>
      api.patch<{ data: AdminUserDetail }>(`/api/v1/admin/users/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-users', id] });
      setIsEditing(false);
      setShowNewProviderForm(false);
      setNewProviderForm(EMPTY_NEW_PROVIDER);
    },
  });

  const createProviderMutation = useMutation({
    mutationFn: (body: { name: string; type: string; contactEmail?: string }) =>
      api.post<CreateProviderResponse>('/api/v1/admin/providers', body),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['providers-list'] });
      setForm((prev) => ({ ...prev, providerId: result.data.id }));
      setShowNewProviderForm(false);
      setNewProviderForm(EMPTY_NEW_PROVIDER);
    },
  });

  const suspendMutation = useMutation({
    mutationFn: () =>
      api.post<{ data: AdminUserDetail }>(`/api/v1/admin/users/${id}/suspend`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-users', id] });
      setConfirmAction(null);
    },
  });

  const activateMutation = useMutation({
    mutationFn: () =>
      api.post<{ data: AdminUserDetail }>(`/api/v1/admin/users/${id}/activate`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-users', id] });
      setConfirmAction(null);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () =>
      api.delete<{ message: string }>(`/api/v1/admin/users/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      navigate('/admin-users');
    },
  });

  // Admin-initiated password reset.
  // Backend: POST /api/v1/admin/users/:id/password
  //  - Requires `admin_user:update`
  //  - Only a super_admin can reset another super_admin's password
  //  - Bumps target's tokenVersion → all active JWTs become invalid
  const resetPasswordMutation = useMutation({
    mutationFn: (body: { password: string; forcePasswordChange: boolean }) =>
      api.post<{ message: string }>(`/api/v1/admin/users/${id}/password`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-users', id] });
    },
  });

  // -- Editing helpers ------------------------------------------------------
  const startEditing = useCallback(() => {
    if (!user) return;
    setForm({
      roleId: user.role.id,
      department: user.department ?? '',
      additionalPermissions: user.additionalPermissions.join(', '),
      providerId: user.providerId ?? '',
    });
    setIsEditing(true);
    setShowNewProviderForm(false);
    setNewProviderForm(EMPTY_NEW_PROVIDER);
    updateMutation.reset();
  }, [user, updateMutation]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setShowNewProviderForm(false);
    setNewProviderForm(EMPTY_NEW_PROVIDER);
    updateMutation.reset();
  }, [updateMutation]);

  const handleEditSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const additionalPerms = form.additionalPermissions
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean);

      updateMutation.mutate({
        roleId: form.roleId || undefined,
        department: form.department || undefined,
        additionalPermissions: additionalPerms,
        providerId: isEditProviderRole
          ? (form.providerId || null)
          : null,
      });
    },
    [form, updateMutation, isEditProviderRole],
  );

  const handleProviderSelectChange = useCallback((value: string) => {
    if (value === CREATE_NEW_PROVIDER_VALUE) {
      setShowNewProviderForm(true);
      setForm((prev) => ({ ...prev, providerId: '' }));
    } else {
      setShowNewProviderForm(false);
      setNewProviderForm(EMPTY_NEW_PROVIDER);
      setForm((prev) => ({ ...prev, providerId: value }));
    }
  }, []);

  const handleCreateProviderSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!newProviderForm.name.trim()) return;
      createProviderMutation.mutate({
        name: newProviderForm.name.trim(),
        type: newProviderForm.type,
        contactEmail: newProviderForm.contactEmail.trim() || undefined,
      });
    },
    [newProviderForm, createProviderMutation],
  );

  const handleConfirmAction = useCallback(() => {
    if (confirmAction === 'suspend') suspendMutation.mutate();
    else if (confirmAction === 'activate') activateMutation.mutate();
    else if (confirmAction === 'remove') removeMutation.mutate();
  }, [confirmAction, suspendMutation, activateMutation, removeMutation]);

  const isActionPending =
    suspendMutation.isPending || activateMutation.isPending || removeMutation.isPending;
  const actionError =
    suspendMutation.error?.message ??
    activateMutation.error?.message ??
    removeMutation.error?.message ??
    null;

  // -- Loading state --------------------------------------------------------
  if (userQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading user details..." />
      </div>
    );
  }

  // -- Error state ----------------------------------------------------------
  if (userQuery.isError || !user) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-red-400">
            {userQuery.isError
              ? `Failed to load user: ${userQuery.error?.message ?? 'Unknown error'}`
              : 'Admin user not found.'}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void userQuery.refetch()}
            >
              Retry
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin-users')}
            >
              Back to Users
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // -- Render ---------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-text-tertiary">
        <Link
          to="/admin-users"
          className="hover:text-text-primary transition-colors"
        >
          Admin Users
        </Link>
        <span>/</span>
        <span className="text-text-primary">{displayName(user)}</span>
      </nav>

      {/* User info card */}
      <Card>
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
          {/* Avatar placeholder */}
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-brand-600/20 text-xl font-bold text-brand-400">
            {initials(user)}
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-semibold text-text-primary">
                {displayName(user)}
              </h2>
              {user.isActive ? (
                <Badge variant="success" size="sm" dot>
                  Active
                </Badge>
              ) : (
                <Badge variant="danger" size="sm" dot>
                  Suspended
                </Badge>
              )}
              <Badge
                variant={user.role.isSystemRole ? 'warning' : 'default'}
                size="sm"
              >
                {user.role.name}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-text-secondary">{user.email}</p>

            {/* Metadata grid */}
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <MetadataItem label="Department">
                <span className="text-sm text-text-primary">
                  {user.department ?? '—'}
                </span>
              </MetadataItem>
              {user.provider && (
                <MetadataItem label="Provider">
                  <Badge variant="primary" size="sm">
                    {user.provider.name}
                  </Badge>
                </MetadataItem>
              )}
              <MetadataItem label="Last Login">
                <span className="text-sm text-text-primary">
                  {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                </span>
              </MetadataItem>
              <MetadataItem label="Created">
                <span className="text-sm text-text-primary">
                  {formatDate(user.createdAt)}
                </span>
              </MetadataItem>
              <MetadataItem label="Updated">
                <span className="text-sm text-text-primary">
                  {formatDate(user.updatedAt)}
                </span>
              </MetadataItem>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {!isEditing && (
              <Button variant="outline" size="sm" onClick={startEditing}>
                Edit
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setResetPassword(EMPTY_RESET_PASSWORD);
                resetPasswordMutation.reset();
              }}
              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
            >
              Reset password
            </Button>
            {user.isActive ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmAction('suspend')}
                className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
              >
                Suspend
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmAction('activate')}
                className="border-green-500/30 text-green-400 hover:bg-green-500/10"
              >
                Activate
              </Button>
            )}
            <Button
              variant="danger"
              size="sm"
              onClick={() => setConfirmAction('remove')}
            >
              Remove Admin Access
            </Button>
          </div>
        </div>
      </Card>

      {/* Edit form card */}
      {isEditing && (
        <Card>
          <CardHeader
            title="Edit Admin User"
            description="Update role, department, provider, or additional permissions."
          />

          <form onSubmit={handleEditSubmit} className="mt-4">
            {updateMutation.isError && (
              <div className="mb-4 rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-400">
                {updateMutation.error?.message ?? 'Failed to update user.'}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-text-primary">
                  Role
                </label>
                <select
                  value={form.roleId}
                  onChange={(e) => {
                    const newRoleId = e.target.value;
                    const newRole = roles.find((r) => r.id === newRoleId);
                    setForm((prev) => ({
                      ...prev,
                      roleId: newRoleId,
                      providerId: newRole?.slug === 'provider_ops' ? prev.providerId : '',
                    }));
                    if (newRole?.slug !== 'provider_ops') {
                      setShowNewProviderForm(false);
                      setNewProviderForm(EMPTY_NEW_PROVIDER);
                    }
                  }}
                  className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                >
                  <option value="">Select a role...</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                      {role.isSystemRole ? ' (System)' : ''}
                    </option>
                  ))}
                </select>
                {rolesQuery.isLoading && (
                  <p className="mt-1 text-xs text-text-tertiary">Loading roles...</p>
                )}
              </div>

              {isEditProviderRole && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-text-primary">
                    Provider <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={showNewProviderForm ? CREATE_NEW_PROVIDER_VALUE : form.providerId}
                    onChange={(e) => handleProviderSelectChange(e.target.value)}
                    className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                  >
                    <option value="">Select a provider...</option>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                    <option value={CREATE_NEW_PROVIDER_VALUE} className="font-medium">
                      + Create New Provider
                    </option>
                  </select>
                  <p className="mt-1 text-xs text-text-tertiary">
                    This user will only see orders assigned to the selected provider.
                  </p>
                </div>
              )}

              <Input
                label="Department"
                value={form.department}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, department: e.target.value }))
                }
                placeholder="e.g. Engineering, Support"
              />

              <Input
                label="Additional Permissions"
                value={form.additionalPermissions}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    additionalPermissions: e.target.value,
                  }))
                }
                placeholder="permission.key, another.key"
                hint="Comma-separated permission keys (optional)"
              />
            </div>

            {/* Inline Create Provider form */}
            {isEditProviderRole && showNewProviderForm && (
              <div className="mt-4 rounded-lg border border-brand-500/30 bg-brand-500/5 p-4">
                <h4 className="text-sm font-semibold text-text-primary">Create New Provider</h4>
                <p className="mt-1 text-xs text-text-secondary">
                  The provider will be created first, then automatically assigned to this user.
                </p>

                {createProviderMutation.isError && (
                  <div className="mt-3 rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-400">
                    {createProviderMutation.error?.message ?? 'Failed to create provider.'}
                  </div>
                )}

                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Input
                    label="Provider Name"
                    value={newProviderForm.name}
                    onChange={(e) =>
                      setNewProviderForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="e.g. Houston DTF"
                    required
                  />
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-text-primary">
                      Type
                    </label>
                    <select
                      value={newProviderForm.type}
                      onChange={(e) =>
                        setNewProviderForm((prev) => ({ ...prev, type: e.target.value }))
                      }
                      className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                    >
                      <option value="EXTERNAL_API">External API</option>
                      <option value="INTERNAL">Internal</option>
                      <option value="MANUAL">Manual</option>
                    </select>
                  </div>
                  <Input
                    label="Contact Email"
                    type="email"
                    value={newProviderForm.contactEmail}
                    onChange={(e) =>
                      setNewProviderForm((prev) => ({ ...prev, contactEmail: e.target.value }))
                    }
                    placeholder="provider@example.com"
                  />
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCreateProviderSubmit}
                    disabled={!newProviderForm.name.trim()}
                    loading={createProviderMutation.isPending}
                  >
                    Create Provider
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowNewProviderForm(false);
                      setNewProviderForm(EMPTY_NEW_PROVIDER);
                    }}
                    disabled={createProviderMutation.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3 border-t border-border pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={cancelEditing}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={updateMutation.isPending}
                disabled={isEditProviderRole && !form.providerId}
              >
                Save Changes
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Effective permissions card */}
      <Card>
        <CardHeader
          title="Effective Permissions"
          description={`This user has ${user.effectivePermissions.length} effective permission${user.effectivePermissions.length !== 1 ? 's' : ''} (role + additional).`}
        />

        {user.additionalPermissions.length > 0 && (
          <div className="mt-4">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
              Additional Permissions
            </h4>
            <div className="flex flex-wrap gap-2">
              {user.additionalPermissions.map((key: string) => (
                <Badge key={key} variant="primary" size="sm">
                  {key}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {user.effectivePermissions.length === 0 ? (
          <div className="mt-4 flex items-center justify-center py-8 text-sm text-text-tertiary">
            No permissions assigned to this user.
          </div>
        ) : permissionsQuery.isLoading ? (
          <div className="mt-4 flex items-center justify-center py-8">
            <Spinner size="sm" label="Loading permission groups..." />
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {groupedPermissions.map((group) => (
              <div key={group.resource}>
                <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-text-primary">
                  {group.label}
                  <Badge variant="default" size="sm">
                    {group.permissions.length}
                  </Badge>
                </h4>
                <div className="flex flex-wrap gap-2">
                  {group.permissions.map((perm: PermissionGroup['permissions'][number]) => {
                    const isAdditional = user.additionalPermissions.includes(
                      perm.key,
                    );
                    return (
                      <Badge
                        key={perm.key}
                        variant={isAdditional ? 'primary' : 'default'}
                        size="sm"
                      >
                        {perm.label}
                        {isAdditional && ' ✦'}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            ))}

            {ungroupedPermissions.length > 0 && (
              <div>
                <h4 className="mb-2 text-sm font-semibold text-text-primary">
                  Other Permissions
                </h4>
                <div className="flex flex-wrap gap-2">
                  {ungroupedPermissions.map((key) => (
                    <Badge key={key} variant="default" size="sm">
                      {key}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Confirmation overlay */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-md rounded-xl border border-border bg-surface p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-text-primary">
              {confirmAction === 'suspend' && 'Suspend Admin User'}
              {confirmAction === 'activate' && 'Activate Admin User'}
              {confirmAction === 'remove' && 'Remove Admin Access'}
            </h3>
            <p className="mt-2 text-sm text-text-secondary">
              {confirmAction === 'suspend' && (
                <>
                  Are you sure you want to suspend{' '}
                  <strong className="text-text-primary">
                    {displayName(user)}
                  </strong>
                  ? They will lose access to the admin panel immediately.
                </>
              )}
              {confirmAction === 'activate' && (
                <>
                  Are you sure you want to reactivate{' '}
                  <strong className="text-text-primary">
                    {displayName(user)}
                  </strong>
                  ? They will regain admin panel access.
                </>
              )}
              {confirmAction === 'remove' && (
                <>
                  Are you sure you want to permanently remove admin access for{' '}
                  <strong className="text-text-primary">
                    {displayName(user)}
                  </strong>
                  ? This action cannot be undone. The underlying user account will
                  not be deleted.
                </>
              )}
            </p>

            {actionError && (
              <div className="mt-3 rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-400">
                {actionError}
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirmAction(null);
                  suspendMutation.reset();
                  activateMutation.reset();
                  removeMutation.reset();
                }}
                disabled={isActionPending}
              >
                Cancel
              </Button>
              <Button
                variant={confirmAction === 'remove' ? 'danger' : 'primary'}
                loading={isActionPending}
                onClick={handleConfirmAction}
              >
                {confirmAction === 'suspend' && 'Suspend User'}
                {confirmAction === 'activate' && 'Activate User'}
                {confirmAction === 'remove' && 'Remove Admin Access'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <form
            className="mx-4 w-full max-w-md rounded-xl border border-border bg-surface p-6 shadow-2xl"
            onSubmit={(e) => {
              e.preventDefault();
              if (!isPasswordValid(resetPassword.password)) return;
              resetPasswordMutation.mutate({
                password: resetPassword.password,
                forcePasswordChange: resetPassword.forcePasswordChange,
              });
            }}
          >
            <h3 className="text-lg font-semibold text-text-primary">
              Reset password
            </h3>
            <p className="mt-2 text-sm text-text-secondary">
              Set a new password for{' '}
              <strong className="text-text-primary">{displayName(user)}</strong>{' '}
              ({user.email}). Their active sessions will be signed out
              immediately and they must use this new password to log back in.
            </p>

            <div className="mt-4">
              <Input
                label="New password"
                type="password"
                value={resetPassword.password}
                onChange={(e) =>
                  setResetPassword((prev) =>
                    prev ? { ...prev, password: e.target.value } : prev,
                  )
                }
                placeholder="Min 8 chars, upper/lower/digit/special"
                required
                autoFocus
                autoComplete="new-password"
                hint="Must contain uppercase, lowercase, number, and special character"
              />
            </div>

            <div className="mt-3 flex items-center gap-2">
              <input
                id="resetForcePasswordChange"
                type="checkbox"
                checked={resetPassword.forcePasswordChange}
                onChange={(e) =>
                  setResetPassword((prev) =>
                    prev
                      ? { ...prev, forcePasswordChange: e.target.checked }
                      : prev,
                  )
                }
                className="h-4 w-4 rounded border-border bg-surface text-brand-500 focus:ring-brand-500/30"
              />
              <label
                htmlFor="resetForcePasswordChange"
                className="text-sm text-text-secondary"
              >
                Require password change on next login
              </label>
            </div>

            {resetPasswordMutation.isError && (
              <div className="mt-3 rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-400">
                {resetPasswordMutation.error?.message ??
                  'Failed to reset password.'}
              </div>
            )}

            {resetPasswordMutation.isSuccess && (
              <div className="mt-3 rounded-md bg-green-500/10 px-4 py-3 text-sm text-green-400">
                Password reset successfully. The user will be signed out of any
                active sessions.
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setResetPassword(null);
                  resetPasswordMutation.reset();
                }}
                disabled={resetPasswordMutation.isPending}
              >
                {resetPasswordMutation.isSuccess ? 'Close' : 'Cancel'}
              </Button>
              {!resetPasswordMutation.isSuccess && (
                <Button
                  type="submit"
                  variant="primary"
                  loading={resetPasswordMutation.isPending}
                  disabled={!isPasswordValid(resetPassword.password)}
                >
                  Reset password
                </Button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MetadataItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wider text-text-tertiary">
        {label}
      </p>
      <div>{children}</div>
    </div>
  );
}
